import { create } from 'zustand';
import {
  uploadDocument,
  checkAnalysisStatus,
  getAnalysisResult,
  loadTestAnalysis,
  getUserAnalyses,
  deleteAnalysis as deleteAnalysisApi,
  compareAnalyses,
  extendAnalysis,
} from '../services/api';
import { trackEvent, trackError } from '../utils/tracking';
import type { AnalysisResult, AnalysisListItem, ComparisonData, DuplicateDialogData, PendingUpload } from '../types';

let _fetchAnalysesInflight: Promise<void> | null = null;

interface AnalysisState {
  results: AnalysisResult | null;
  currentJobId: string | null;
  loading: boolean;
  loadingMode: 'analyzing' | 'opening';
  error: string | null;
  analysesList: AnalysisListItem[];
  historyLoading: boolean;
  fileName: string;
  currentCompanyName: string;
  comparisonData: ComparisonData | null;
  comparisonLoading: boolean;
  extendingAnalysisId: string | null;
  extensionGuidance: string;
  duplicateDialogData: DuplicateDialogData | null;
  pendingUpload: PendingUpload | null;
}

interface AnalysisActions {
  setResults: (results: AnalysisResult | null) => void;
  setCurrentJobId: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setLoadingMode: (mode: 'analyzing' | 'opening') => void;
  setError: (error: string | null) => void;
  setFileName: (name: string) => void;
  setCurrentCompanyName: (name: string) => void;
  setExtendingAnalysisId: (id: string | null) => void;
  setExtensionGuidance: (guidance: string) => void;
  setDuplicateDialogData: (data: DuplicateDialogData | null) => void;
  setPendingUpload: (data: PendingUpload | null) => void;
  setComparisonData: (data: ComparisonData | null) => void;
  fetchAnalysesList: () => Promise<void>;
  handleCompare: (id1: string, id2: string) => Promise<void>;
  pollAnalysisStatus: (jobId: string, startTime: number, pollTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => void;
}

export const useAnalysisStore = create<AnalysisState & AnalysisActions>((set, get) => ({
  // State
  results: null,
  currentJobId: null,
  loading: false,
  loadingMode: 'analyzing',
  error: null,
  analysesList: [],
  historyLoading: false,
  fileName: '',
  currentCompanyName: '',
  comparisonData: null,
  comparisonLoading: false,
  extendingAnalysisId: null,
  extensionGuidance: '',
  duplicateDialogData: null,
  pendingUpload: null,

  // Simple setters
  setResults: (results) => set({ results }),
  setCurrentJobId: (id) => set({ currentJobId: id }),
  setLoading: (loading) => set({ loading }),
  setLoadingMode: (mode) => set({ loadingMode: mode }),
  setError: (error) => set({ error }),
  setFileName: (name) => set({ fileName: name }),
  setCurrentCompanyName: (name) => set({ currentCompanyName: name }),
  setExtendingAnalysisId: (id) => set({ extendingAnalysisId: id }),
  setExtensionGuidance: (guidance) => set({ extensionGuidance: guidance }),
  setDuplicateDialogData: (data) => set({ duplicateDialogData: data }),
  setPendingUpload: (data) => set({ pendingUpload: data }),
  setComparisonData: (data) => set({ comparisonData: data }),

  // Async actions
  fetchAnalysesList: () => {
    if (_fetchAnalysesInflight) return _fetchAnalysesInflight;
    _fetchAnalysesInflight = (async () => {
      set({ historyLoading: true });
      try {
        const response = await getUserAnalyses();
        set({ analysesList: response.analyses || [] });
      } catch (err) {
        console.error('Error fetching analyses list:', err);
        set({ analysesList: [] });
      } finally {
        set({ historyLoading: false });
        _fetchAnalysesInflight = null;
      }
    })();
    return _fetchAnalysesInflight;
  },

  handleCompare: async (id1, id2) => {
    set({ comparisonLoading: true });
    try {
      trackEvent('Comparison Started', { id1, id2 });
      const data = await compareAnalyses(id1, id2);
      set({
        comparisonData: data,
        results: null,
      });
      trackEvent('Comparison Completed', { id1, id2 });
    } catch (error: any) {
      console.error('Comparison error:', error);
      trackError('Comparison Failed', error.message);
      throw error; // Let caller handle toast
    } finally {
      set({ comparisonLoading: false });
    }
  },

  pollAnalysisStatus: (jobId, startTime, pollTimeoutRef) => {
    let pollCount = 0;

    const checkStatus = async () => {
      try {
        const status = await checkAnalysisStatus(jobId);
        pollCount++;

        if (status.status === 'completed') {
          pollTimeoutRef.current = null;
          const result = await getAnalysisResult(jobId);
          set({ results: result, loading: false, currentJobId: jobId });
          const analysisTime = Date.now() - (startTime || Date.now());
          trackEvent('Analysis Completed', { jobId, duration: analysisTime, pollCount });
          get().fetchAnalysesList();
        } else if (status.status === 'failed') {
          pollTimeoutRef.current = null;
          trackError('Analysis Failed', status.error || 'Unknown error');
          set({ error: status.error || 'Analysis failed', loading: false, currentJobId: null });
        } else {
          const nextDelay = pollCount < 6 ? 5000 : 10000;
          pollTimeoutRef.current = setTimeout(checkStatus, nextDelay);
        }
      } catch (err: any) {
        pollTimeoutRef.current = null;
        console.error('Status check error:', err);
        trackError('Status Check Failed', err.message);
        set({ error: 'Failed to check analysis status', loading: false, currentJobId: null });
      }
    };

    pollTimeoutRef.current = setTimeout(checkStatus, 3000);
  },
}));
